import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInbox } from '@fortawesome/pro-light-svg-icons';
import { Modal } from '@material-ui/core';
import { Channel } from '@spike/omnichannel-model';
import { showError } from '@spike/notifications-action';
import AddNewClient from 'components/SMS/TwoWay/ProspectMessage/Chat/AddNewClient';
import CreateBookingDrawer from 'components/UI/CreateBookingDrawer';
import Spinner from 'components/UI/V3/Spinner';
import { useAuth } from 'hooks';
import useIsMobile from 'hooks/useIsMobile';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useMessagesChannelMarketplace } from 'WebSockets/MessagesChannelMarketplaceContext';
import Composer from './Composer/Composer';
import ConversationList from './ConversationList/ConversationList';
import InboxSettingsDrawer from './InboxSettings/InboxSettingsDrawer';
import NewConversationPane from './NewMessage/NewConversationPane';
import { phoneDigits } from './NewMessage/recipient';
import { useScopedApiClientWrapper } from './useScopedApiClientWrapper';
import MessageThread from './MessageThread/MessageThread';
import ThreadHeader from './MessageThread/ThreadHeader';
import { useInbox } from './useInbox';
import styles from './OmnichannelInbox.module.scss';

const lastInboundChannel = (
    messages: Array<{ kind: string; direction: string; channel: Channel }>
): Channel | undefined => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
        const message = messages[index];
        if (message.kind === 'message' && message.direction === 'inbound') {
            return message.channel;
        }
    }
    return undefined;
};

export const OmnichannelInbox: React.FC = () => {
    const dispatch = useDispatch();
    const auth = useAuth();
    const isMobile = useIsMobile();
    const location = useLocation<{ conversationId?: number }>();
    const [mobileThreadOpen, setMobileThreadOpen] = useState(() => typeof location.state?.conversationId === 'number');
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [createClientPhone, setCreateClientPhone] = useState<string | null>(null);
    const [bookingOpen, setBookingOpen] = useState(false);

    // Multi-location: same mechanism as legacy /sms (TwoWay.tsx). The override drives the InboxChannel
    // subscription (InboxWebSockets.tsx); the login marketplace itself is never changed here.
    const [selectedMarketplaceId, setSelectedMarketplaceId] = useState(auth.marketplaceId!);
    const { setMessagesMarketplaceOverride } = useMessagesChannelMarketplace();

    useEffect(() => {
        setMessagesMarketplaceOverride(selectedMarketplaceId);

        return () => {
            setMessagesMarketplaceOverride(undefined);
        };
    }, [selectedMarketplaceId, setMessagesMarketplaceOverride]);

    const inbox = useInbox(selectedMarketplaceId);
    const scopedApiClientWrapper = useScopedApiClientWrapper(selectedMarketplaceId);
    const selectedLocationName = auth.marketplaces?.find(marketplace => marketplace.id === selectedMarketplaceId)?.name;

    const handleMarketplaceChange = useCallback((marketplaceId: number) => {
        setSelectedMarketplaceId(marketplaceId);
        setMobileThreadOpen(false);
        setBookingOpen(false);
        setCreateClientPhone(null);
    }, []);

    const handleSelectConversation = (id: number) => {
        inbox.selectConversation(id);
        setMobileThreadOpen(true);
        setBookingOpen(false);
        setCreateClientPhone(null);
    };

    const handleNewMessage = () => {
        inbox.startNewConversation();
        setMobileThreadOpen(true);
        setBookingOpen(false);
    };

    const handleCancelNewConversation = () => {
        inbox.cancelNewConversation();
        setMobileThreadOpen(false);
    };

    const handleCreateClient = () => {
        const phone = inbox.selectedConversation?.contact.phone ?? '';
        const digits = phoneDigits(phone);

        if (!digits) {
            dispatch(showError('This conversation has no phone number, so a client cannot be created from it.'));
            return;
        }

        setCreateClientPhone(digits);
    };

    const handleClientCreated = () => {
        const phone = createClientPhone;
        setCreateClientPhone(null);
        if (phone) {
            inbox.refreshAfterClientCreated(phone);
        }
    };

    const showList = !isMobile || !mobileThreadOpen;
    const showThread = !isMobile || mobileThreadOpen;

    // CreateBooking always books in the login marketplace (it reads `useApiClientWrapper`/`useMarketplace`,
    // with no way to scope it), so booking is only offered while the inbox is on that same location.
    // Showing it on another location would silently create the appointment in the wrong one.
    const canBookHere = selectedMarketplaceId === auth.marketplaceId;
    const bookingContact =
        inbox.selectedConversation?.contact.type === 'customer' ? inbox.selectedConversation.contact : undefined;

    return (
        <div className={styles.root}>
            {showList && (
                <ConversationList
                    conversations={inbox.conversations}
                    selectedId={inbox.selectedId}
                    searchText={inbox.searchText}
                    channelFilter={inbox.channelFilter}
                    totalUnread={inbox.totalUnread}
                    loading={inbox.isListLoading}
                    errorMessage={inbox.listError}
                    newConversationIds={inbox.newConversationIds}
                    selectedMarketplaceId={selectedMarketplaceId}
                    onMarketplaceChange={handleMarketplaceChange}
                    onOpenSettings={() => setSettingsOpen(true)}
                    onNewMessage={handleNewMessage}
                    onSelect={handleSelectConversation}
                    onSearchTextChange={inbox.setSearchText}
                    onChannelFilterChange={inbox.setChannelFilter}
                />
            )}

            {showThread && inbox.composing && (
                <NewConversationPane
                    apiClientWrapper={scopedApiClientWrapper}
                    inboxes={inbox.inboxes}
                    sending={inbox.isSending}
                    error={inbox.composeError}
                    onSend={inbox.sendFirstMessage}
                    onPickRecipient={inbox.openExistingConversation}
                    onCancel={handleCancelNewConversation}
                    onBack={isMobile ? handleCancelNewConversation : undefined}
                />
            )}

            {showThread &&
                !inbox.composing &&
                (inbox.selectedId === undefined ? (
                    <section className={styles.emptyThread}>
                        <FontAwesomeIcon
                            icon={faInbox}
                            className={styles.emptyIcon}
                        />
                        <p>Select a conversation to get started</p>
                    </section>
                ) : !inbox.selectedConversation ? (
                    <section className={styles.emptyThread}>
                        <Spinner />
                    </section>
                ) : (
                    <section className={styles.thread}>
                        <ThreadHeader
                            contact={inbox.selectedConversation.contact}
                            conversationId={inbox.selectedId}
                            onBack={isMobile ? () => setMobileThreadOpen(false) : undefined}
                            onCreateClient={handleCreateClient}
                            onBook={canBookHere ? () => setBookingOpen(true) : undefined}
                        />
                        <MessageThread
                            conversation={inbox.selectedConversation}
                            onAttachmentsExpired={inbox.refreshSelectedConversation}
                            onLoadOlder={inbox.loadOlderMessages}
                            loadingOlder={inbox.loadingOlder}
                            hasOlder={inbox.hasOlder}
                        />
                        <Composer
                            contact={inbox.selectedConversation.contact}
                            inboxes={
                                inbox.selectedConversation.inboxes.length > 0
                                    ? inbox.selectedConversation.inboxes
                                    : inbox.inboxes
                            }
                            sending={inbox.isSending}
                            sendError={inbox.sendError}
                            defaultChannel={lastInboundChannel(inbox.selectedConversation.messages)}
                            onSend={inbox.sendMessage}
                        />
                    </section>
                ))}

            {createClientPhone && inbox.selectedConversation?.contact.type === 'prospect' && (
                <Modal
                    open
                    className={styles.createClientModal}
                    onClose={() => setCreateClientPhone(null)}
                >
                    <AddNewClient
                        prospect={{
                            id: inbox.selectedConversation.contact.id,
                            phone: createClientPhone
                        }}
                        marketplaceId={selectedMarketplaceId}
                        mode="client"
                        apiClientWrapper={scopedApiClientWrapper}
                        refreshProspectConversations={false}
                        onSaveNewClient={handleClientCreated}
                        onClose={() => setCreateClientPhone(null)}
                    />
                </Modal>
            )}

            {/* Same drawer the legacy /sms chat opens: no `fromClient` and no `petID`, so "skip and book" stays available. */}
            {bookingOpen && bookingContact && canBookHere && (
                <CreateBookingDrawer
                    parentID={bookingContact.id}
                    parentName={bookingContact.name}
                    showTabs={false}
                    onClose={() => setBookingOpen(false)}
                    onBooked={() => setBookingOpen(false)}
                />
            )}

            {settingsOpen && (
                <InboxSettingsDrawer
                    marketplaceId={selectedMarketplaceId}
                    locationName={selectedLocationName}
                    inboxes={inbox.inboxes}
                    onClose={() => setSettingsOpen(false)}
                />
            )}
        </div>
    );
};

export default OmnichannelInbox;
